import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { DataStore, Hub, syncExpression } from 'aws-amplify';
import { addBreadcrumb, captureError, captureMessage } from '../common/sentry';
import { useAlert } from './useAlert';
import { UserDetailsDataProps, userDetailsData } from '../datastore/userDetails';
import { SSCCsDataProps, ssccsData } from '../datastore/ssccs';
import { LocationDataProps, locationData } from '../datastore/locations';
import { PrinterDataProps, printersData } from '../datastore/printers';
import { PackageTypeDataProps, packageTypeData } from '../datastore/packageType';
import { QMReasonDataProps, qmReasonData } from '../datastore/qmReason';
import { QMIssueDataProps, qmIssueData } from '../datastore/qmIssue';
import { WorkflowStatusDataProps, workflowStatusData } from '../datastore/workflowStatus';
import { TripSheetsDataProps, tripSheetsData } from '../datastore/tripSheets';
import { RemoveItemSSCCHistoryDataProps, removeItemSSCCHistory } from '../datastore/removeItemSSCCHistory';
import { ReceiptEventPackageHistoryDataProps, receiptEventPackageHistory } from '../datastore/receiptEventHistory';
import { AdminActionHistoryDataProps, adminActionHistory } from '../datastore/adminActionHistory';
import { TransportBookingEventDataProps, transportBookingEventData } from '../datastore/transportBookingEvent';
import {
  AdminActionHistory,
  ReceiptEventPackageHistory,
  RemoveItemSSCCHistory,
  SSCC,
  TripSheet,
  TransportBookingEvent,
  UserDetails,
  Location,
  Printer,
  PackageType,
  QMReason,
  QMIssue,
  WorkflowStatus
} from '../models';
import { SsccStatus } from '../common';
import { daysAgoAtStartOfDay, monthsAgoAtStartOfDay, NEVER_SYNC_FUTURE_DATE } from '../common/syncPredicateDates';
import { consumeForceResyncOnBoot } from '../common/helperFunctions/localStorage';

export interface DatastoreContextProps {
  isDatastoreReady: boolean;
  isSyncing: boolean;
  modelReadiness: ModelReadiness;
  isEssentialReady: boolean;
  syncProgress: {
    [key: string]: { new: number; updated: number; deleted: number };
  };
  syncDurations: Record<string, number>;
  lastFullSyncAt: Record<string, number>;
  syncedRecordCounts: Record<string, number>;
  userDetails: UserDetailsDataProps;
  ssccs: SSCCsDataProps;
  tripSheets: TripSheetsDataProps;
  locations: LocationDataProps;
  printers: PrinterDataProps;
  packageTypes: PackageTypeDataProps;
  qmReasons: QMReasonDataProps;
  qmIssues: QMIssueDataProps;
  removeItemSSCCHistories: RemoveItemSSCCHistoryDataProps;
  workflowStatuses: WorkflowStatusDataProps;
  receiptEventPackageHistories: ReceiptEventPackageHistoryDataProps;
  adminActionHistories: AdminActionHistoryDataProps;
  transportBookingEvents: TransportBookingEventDataProps;
}

interface DatastoreProviderProps {
  children?: React.ReactNode;
}

const defaultDatastoreConfiguration = {
  maxRecordsToSync: 50000,
  fullSyncInterval: +(process.env.REACT_APP_OVERRIDE_FULL_SYNC_MINUTES ?? 60 * 2),
  syncPageSize: 80
};

const MACHINE_ID_KEY = 'ftl.machineId';
const SESSION_ID_KEY = 'ftl.sessionId';
const LAST_SYNC_START_MS_KEY = 'ftl.lastDataStoreSyncStartMs';
let hasDataStoreCompletedInitialSync = false;

// Tracks an in-flight boot-time `DataStore.clear()` (force-resync) at module scope so
// that a second provider mount — e.g. React 18 StrictMode's dev double-invoke, or a
// fast remount — awaits the SAME clear before calling `DataStore.start()`, instead of
// starting while DataStore is still "Clearing" (which throws DataStoreStateError).
let pendingClearPromise: Promise<void> | null = null;

export type ModelReadiness = Record<string, boolean>;

// Small master-data models that hydrate in seconds. The app is usable once these are
// ready; heavy transactional models (SSCC, TripSheet, ...) keep syncing in background.
export const ESSENTIAL_MODELS = [
  'UserDetails',
  'Location',
  'Printer',
  'PackageType',
  'QMReason',
  'QMIssue',
  'WorkflowStatus'
] as const;

export function markModelReady(prev: ModelReadiness, modelName: string): ModelReadiness {
  if (prev[modelName]) return prev;
  return { ...prev, [modelName]: true };
}

export function areEssentialModelsReady(readiness: ModelReadiness): boolean {
  return ESSENTIAL_MODELS.every((m) => readiness[m] === true);
}

// Full enumeration of every DataStore model (essential + heavy). This is the
// authoritative source the sync-diagnostics panel iterates over.
export const MODEL_REGISTRY = [
  ...ESSENTIAL_MODELS,
  'SSCC',
  'TripSheet',
  'TransportBookingEvent',
  'AdminActionHistory',
  'ReceiptEventPackageHistory',
  'RemoveItemSSCCHistory'
] as const;

// name -> model class, used for diagnostic record-count queries. Keep aligned with MODEL_REGISTRY.
const MODEL_CLASS_BY_NAME: Record<string, any> = {
  UserDetails,
  Location,
  Printer,
  PackageType,
  QMReason,
  QMIssue,
  WorkflowStatus,
  SSCC,
  TripSheet,
  TransportBookingEvent,
  AdminActionHistory,
  ReceiptEventPackageHistory,
  RemoveItemSSCCHistory
};

export function computeSyncDurationMs(cycleStartMs: number | null, nowMs: number): number | null {
  if (!cycleStartMs || nowMs < cycleStartMs) return null;
  return nowMs - cycleStartMs;
}

const LAST_FULL_SYNC_MS_KEY_PREFIX = 'ftl.lastFullSyncMs.';

export function readLastFullSyncMap(models: readonly string[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const m of models) {
    const raw = getStorageItemSafe(localStorage, `${LAST_FULL_SYNC_MS_KEY_PREFIX}${m}`);
    const n = raw ? Number(raw) : NaN;
    if (Number.isFinite(n)) out[m] = n;
  }
  return out;
}

export function setLastFullSyncMs(model: string, ms: number): void {
  setStorageItemSafe(localStorage, `${LAST_FULL_SYNC_MS_KEY_PREFIX}${model}`, String(ms));
}

const isLocalDevelopment =
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_LOCAL_ADMIN === 'true' &&
  process.env.REACT_APP_ALLOW_SYNC_ALL_DATA !== 'true';

function createStableClientId(prefix: string): string {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return `${prefix}-${window.crypto.randomUUID()}`;
  }

  return `${prefix}-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

function getStorageItemSafe(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function setStorageItemSafe(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    // Telemetry should not fail app runtime if storage is unavailable.
  }
}

function getMachineId(): string {
  const existing = getStorageItemSafe(localStorage, MACHINE_ID_KEY);
  if (existing) {
    return existing;
  }

  const generated = createStableClientId('m');
  setStorageItemSafe(localStorage, MACHINE_ID_KEY, generated);
  return generated;
}

function getSessionId(): string {
  const existing = getStorageItemSafe(sessionStorage, SESSION_ID_KEY);
  if (existing) {
    return existing;
  }

  const generated = createStableClientId('s');
  setStorageItemSafe(sessionStorage, SESSION_ID_KEY, generated);
  return generated;
}

function getNavigationType(): string {
  const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  return navEntry?.type || 'unknown';
}

const activeHistoricalSsccStatuses = [
  SsccStatus.open,
  SsccStatus.staged,
  SsccStatus.manifested,
  SsccStatus.inTransit,
  SsccStatus.approaching
];

const DatastoreContext = createContext<DatastoreContextProps>({} as DatastoreContextProps);

const useDatastore = () => useContext(DatastoreContext);

function resetDataStoreInitialSyncState() {
  hasDataStoreCompletedInitialSync = false;
}

function getDataStoreMutationFailureDetail(data: any): string {
  return (
    data?.error?.message ||
    data?.errorMessage ||
    data?.message ||
    data?.errorType ||
    'The server rejected the DataStore mutation.'
  );
}

const DatastoreProvider = ({ children }: DatastoreProviderProps) => {
  const alert = useAlert();
  const [isDatastoreReady, setIsDatastoreReady] = useState(hasDataStoreCompletedInitialSync);
  const [isSyncing, setIsSyncing] = useState(true);
  const [newRecordCounts, setNewRecordCounts] = useState<{
    [key: string]: { new: number; updated: number; deleted: number };
  }>({});
  // One-way latch: models are marked ready on first modelSynced and never cleared.
  // isEssentialReady stays true across re-sync cycles by design — it is a "safe to use"
  // gate, not a "currently syncing" indicator.
  const [modelReadiness, setModelReadiness] = useState<ModelReadiness>({});

  // Diagnostics state (sync-panel). Additive — does not affect readiness/telemetry.
  const cycleStartMsRef = useRef<number | null>(null);
  const [syncDurations, setSyncDurations] = useState<Record<string, number>>({});
  const [lastFullSyncAt, setLastFullSyncAt] = useState<Record<string, number>>(() =>
    readLastFullSyncMap(MODEL_REGISTRY)
  );
  const [syncedRecordCounts, setSyncedRecordCounts] = useState<Record<string, number>>({});

  const userDetails = userDetailsData();
  const ssccs = ssccsData();
  const tripSheets = tripSheetsData();
  const locations = locationData();
  const printers = printersData();
  const packageTypes = packageTypeData();
  const qmReasons = qmReasonData();
  const qmIssues = qmIssueData();
  const transportBookingEvents = transportBookingEventData();
  const workflowStatuses = workflowStatusData();
  const removeItemSSCCHistories = removeItemSSCCHistory();
  const receiptEventPackageHistories = receiptEventPackageHistory();
  const adminActionHistories = adminActionHistory();
  const days = +(process.env.SSCC_MANIFEST_FROM_LAST_X_DAYS ?? 60);
  // Predicate dates must be stable across app restarts: any change to the serialized
  // sync predicate forces DataStore to discard its delta-sync checkpoint and full-sync
  // the model (3-4 min). See syncPredicateDates.ts.
  const timePredicate = daysAgoAtStartOfDay(days);
  const localOneMonthPredicate = monthsAgoAtStartOfDay(1);
  const futureDate = NEVER_SYNC_FUTURE_DATE;

  useEffect(() => {
    const appStartAtMs = Date.now();
    const machineId = getMachineId();
    const sessionId = getSessionId();
    const navigationType = getNavigationType();
    let hasSeenSyncStart = false;
    let sawOfflineSinceLastSyncStart = false;
    let syncCycleSequence = 0;
    let currentSyncCycleId = '';
    let currentSyncCycleCause = 'scheduled-or-maintenance-sync';
    let hasReportedFullSyncInCurrentCycle = false;

    addBreadcrumb('DataStore telemetry context initialized', {
      category: 'datastore.sync.telemetry',
      level: 'info',
      data: {
        machineId,
        sessionId,
        navigationType
      }
    });

    const listener = Hub.listen('datastore', async ({ payload: { event, data } }) => {
      console.info('datastore event', event, data);
      switch (event) {
        case 'ready':
          console.log(event);
          hasDataStoreCompletedInitialSync = true;
          setIsDatastoreReady(true);
          // Essential-model names must match the runtime modelSynced model.name;
          // syncQueriesReady backstop releases the gate if they ever diverge.
          setModelReadiness((prev) => ESSENTIAL_MODELS.reduce((acc, m) => markModelReady(acc, m), prev));
          setIsSyncing(false);
          break;
        case 'syncQueriesStarted':
          setIsSyncing(true);
          const nowMs = Date.now();
          cycleStartMsRef.current = nowMs;
          const previousSyncStartRaw = getStorageItemSafe(localStorage, LAST_SYNC_START_MS_KEY);
          const previousSyncStartMs = previousSyncStartRaw ? Number(previousSyncStartRaw) : null;
          const previousSyncStartAgoMs =
            previousSyncStartMs && Number.isFinite(previousSyncStartMs) ? nowMs - previousSyncStartMs : null;

          setStorageItemSafe(localStorage, LAST_SYNC_START_MS_KEY, String(nowMs));

          const isReload = navigationType === 'reload';
          let cause = 'scheduled-or-maintenance-sync';

          if (!hasSeenSyncStart) {
            if (isReload) {
              cause = 'startup-after-page-refresh';
            } else {
              cause = 'startup-initial-sync';
            }
          } else if (sawOfflineSinceLastSyncStart) {
            cause = 'reconnect-after-offline';
          }

          hasSeenSyncStart = true;
          sawOfflineSinceLastSyncStart = false;
          syncCycleSequence += 1;
          currentSyncCycleId = `${sessionId}-${syncCycleSequence}`;
          currentSyncCycleCause = cause;
          hasReportedFullSyncInCurrentCycle = false;

          captureMessage('datastore-sync-started', {
            level: 'info',
            context: 'datastore-sync-started',
            tags: {
              sync_event: 'datastore_sync_started',
              machine_id: machineId,
              cause,
              page_navigation_type: navigationType,
              page_reloaded: String(isReload)
            },
            extra: {
              syncCycleId: currentSyncCycleId,
              sessionId,
              appStartAtMs,
              appUptimeMs: nowMs - appStartAtMs,
              fullSyncIntervalMinutes: defaultDatastoreConfiguration.fullSyncInterval,
              previousSyncStartAgoMs,
              datastoreReadyBeforeSyncStart: isDatastoreReady,
              browserOnline: navigator.onLine,
              note_ip_collection: 'enabled via sendDefaultPii'
            }
          });

          addBreadcrumb('DataStore sync started', { category: 'datastore.sync', level: 'info' });
          break;
        case 'syncQueriesReady':
          hasDataStoreCompletedInitialSync = true;
          setIsDatastoreReady(true);
          // Essential-model names must match the runtime modelSynced model.name;
          // syncQueriesReady backstop releases the gate if they ever diverge.
          setModelReadiness((prev) => ESSENTIAL_MODELS.reduce((acc, m) => markModelReady(acc, m), prev));
          setIsSyncing(false);
          addBreadcrumb('DataStore sync completed', { category: 'datastore.sync', level: 'info' });
          break;
        case 'outboxMutationFailed':
          console.error('DataStore outbox mutation failed:', data);
          const mutationFailureDetail = getDataStoreMutationFailureDetail(data);
          const mutationFailureModel = data?.model?.name || data?.model || 'unknown';
          captureError(
            new Error(`DataStore outbox mutation failed: ${mutationFailureModel} - ${mutationFailureDetail}`),
            'datastore-outbox-mutation-failed',
            {
              tags: {
                datastore_event: 'outboxMutationFailed',
                datastore_model: String(mutationFailureModel),
                datastore_error_type: String(data?.errorType || 'unknown')
              },
              extra: {
                syncCycleId: currentSyncCycleId || null,
                sessionId,
                model: mutationFailureModel,
                errorType: data?.errorType || null,
                errorMessage: mutationFailureDetail,
                operation: data?.operation || null,
                graphqlErrors: data?.error?.errors || null,
                rawEventData: data
              }
            }
          );
          alert(
            `Sync failed: ${mutationFailureDetail}. Your change was saved locally but could not sync to the server. Please refresh and try again.`,
            'error',
            { width: '34em' }
          );
          break;
        case 'networkStatus':
          if (data?.active === false) {
            console.warn('DataStore network status: offline');
            sawOfflineSinceLastSyncStart = true;
          }
          break;
        case 'modelSynced':
          const {
            counts,
            isFullSync,
            model: { name: modelName }
          } = data;

          setModelReadiness((prev) => markModelReady(prev, modelName));

          if (isFullSync && !hasReportedFullSyncInCurrentCycle) {
            hasReportedFullSyncInCurrentCycle = true;

            captureMessage('datastore-full-sync-detected', {
              level: 'info',
              context: 'datastore-full-sync-detected',
              tags: {
                sync_event: 'datastore_full_sync_detected',
                machine_id: machineId,
                cause: currentSyncCycleCause,
                page_navigation_type: navigationType,
                page_reloaded: String(navigationType === 'reload')
              },
              extra: {
                syncCycleId: currentSyncCycleId,
                sessionId,
                firstModelReportingFullSync: modelName,
                appUptimeMs: Date.now() - appStartAtMs,
                browserOnline: navigator.onLine,
                note_ip_collection: 'enabled via sendDefaultPii'
              }
            });
          }

          if (counts.new > 0 || counts.updated > 0 || counts.deleted > 0) {
            setNewRecordCounts((prev) => ({
              ...prev,
              [modelName]: counts
            }));
          }

          // Diagnostics (additive): per-model sync timing, last-full-sync, and a lazy
          // record-count query. All guarded; failures are swallowed (diagnostic-only).
          const syncEndMs = Date.now();
          const dur = computeSyncDurationMs(cycleStartMsRef.current, syncEndMs);
          if (dur !== null) setSyncDurations((prev) => ({ ...prev, [modelName]: dur }));
          if (isFullSync) {
            setLastFullSyncMs(modelName, syncEndMs);
            setLastFullSyncAt((prev) => ({ ...prev, [modelName]: syncEndMs }));
          }
          const modelClass = MODEL_CLASS_BY_NAME[modelName];
          if (modelClass) {
            DataStore.query(modelClass)
              .then((records) => setSyncedRecordCounts((prev) => ({ ...prev, [modelName]: records.length })))
              .catch(() => {
                /* diagnostic only; ignore */
              });
          }
          break;
      }
    });

    const syncExpressions = [
      syncExpression(TransportBookingEvent, () => {
        const transportBookingEventStartDate = isLocalDevelopment ? localOneMonthPredicate : monthsAgoAtStartOfDay(6);
        console.log('TransportBookingEvent sync expression - filtering records after:', transportBookingEventStartDate);
        return (tbe) => tbe?.createdAt.gt(transportBookingEventStartDate);
      }),
      syncExpression(TripSheet, () => {
        const tripSheetStartDate = isLocalDevelopment ? localOneMonthPredicate : monthsAgoAtStartOfDay(6);
        console.log('TripSheet sync expression - filtering records after:', tripSheetStartDate);
        return (ts) => ts?.createdAt.gt(tripSheetStartDate);
      }),
      syncExpression(SSCC, () => {
        if (isLocalDevelopment) {
          return (sscc) => sscc?.createdAt.ge(localOneMonthPredicate);
        }

        return (sscc) =>
          sscc.or((sscc) => [
            sscc?.createdAt.ge(timePredicate),
            ...activeHistoricalSsccStatuses.map((status) =>
              sscc.and((sscc) => [sscc?.createdAt.lt(timePredicate), sscc?.status.eq(status)])
            )
          ]);
      }),
      syncExpression(AdminActionHistory, () => {
        const auditLogStartDate = isLocalDevelopment ? localOneMonthPredicate : monthsAgoAtStartOfDay(6);
        console.log('AdminActionHistory sync expression - filtering records after:', auditLogStartDate);
        return (a) => a?.actionTime.gt(auditLogStartDate);
      }),
      syncExpression(ReceiptEventPackageHistory, () => {
        return (r) => r?.actionTime.gt(futureDate);
      }),
      syncExpression(RemoveItemSSCCHistory, () => {
        return (r) => r?.actionTime.gt(futureDate);
      })
    ];

    //Datastore documentation: https://docs.amplify.aws/lib/datastore/getting-started/q/platform/js/
    const startDataStore = async () => {
      try {
        // Honour a force-resync requested from the sync popover: wipe the local store
        // now, before start(), so the sync engine isn't running (clear is fast/safe
        // here — the same path as logout→login). This guarantees a true full re-sync.
        // Only the mount that consumes the flag kicks off the clear; any concurrent
        // mount awaits that same promise so start() never runs during "Clearing".
        if (consumeForceResyncOnBoot()) {
          pendingClearPromise = DataStore.clear();
        }
        if (pendingClearPromise) {
          await pendingClearPromise;
          pendingClearPromise = null;
        }
        DataStore.configure({ ...defaultDatastoreConfiguration, syncExpressions });
        DataStore.start();
      } catch (error) {
        console.error('Failed to initialize DataStore:', error);
        captureError(error, 'datastore-initialize');
        setIsDatastoreReady(false);
        setIsSyncing(true);
      }
    };
    startDataStore();

    return () => {
      listener();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isEssentialReady = areEssentialModelsReady(modelReadiness);

  const providerMountedAtMsRef = useRef<number>(Date.now());
  const reportedEssentialReadyRef = useRef<boolean>(false);

  useEffect(() => {
    if (!isEssentialReady || reportedEssentialReadyRef.current) return;
    reportedEssentialReadyRef.current = true;
    captureMessage('datastore-essential-ready', {
      level: 'info',
      context: 'datastore-essential-ready',
      tags: { sync_event: 'datastore_essential_ready' },
      extra: { msSinceProviderMount: Date.now() - providerMountedAtMsRef.current }
    });
  }, [isEssentialReady]);

  const value = {
    isDatastoreReady: isDatastoreReady,
    isSyncing: isSyncing,
    modelReadiness,
    isEssentialReady,
    syncProgress: newRecordCounts,
    syncDurations,
    lastFullSyncAt,
    syncedRecordCounts,
    userDetails,
    ssccs,
    tripSheets,
    locations,
    printers,
    packageTypes,
    qmIssues,
    qmReasons,
    workflowStatuses,
    removeItemSSCCHistories,
    receiptEventPackageHistories,
    adminActionHistories,
    transportBookingEvents
  };

  return <DatastoreContext.Provider value={value}>{children}</DatastoreContext.Provider>;
};

export { useDatastore, DatastoreProvider, resetDataStoreInitialSyncState };
